export type AuditProgramOrgType = 'HOME_CARE' | 'RESIDENTIAL' | 'MIXED';
export type AuditProgramScope = AuditProgramOrgType | 'HOME_COMMUNITY' | 'MIXED_WORKSPACE';
export type AuditVisibilityScope = 'HOME_COMMUNITY' | 'RESIDENTIAL';
export type AuditVisibilityReviewState = 'READY' | 'REVIEW_REQUIRED' | 'UNAVAILABLE';

/** Minimal provider-fact projection used to decide audit reachability. */
export interface AuditVisibilityContext {
  /** The web projection uses camelCase; the RPC payload remains snake_case. */
  auditScopes?: readonly AuditVisibilityScope[];
  audit_scopes?: readonly AuditVisibilityScope[];
  reviewState?: AuditVisibilityReviewState;
  review_state?: AuditVisibilityReviewState;
}

export interface ResolvedAuditVisibility {
  scopes: AuditVisibilityScope[];
  phases: AuditProgramPhase[];
  reviewState: AuditVisibilityReviewState;
  showReview: boolean;
}

export type AuditProgramPhase = 'sah' | 'rac_pending' | 'rac' | 'unknown';

/**
 * RAC audit-program surfaces are enabled for RESIDENTIAL tenants as of
 * path-to-go S10 (13-Jul RAC conditions). Previously deferred "until the RAC
 * release"; work-queue surfacing is now live alongside writes.
 */
export const AUDIT_PROGRAM_RAC_WORK_QUEUE_ENABLED = true;

/** Facility types in scope for the SAH-first audit program release. */
export const SAH_AUDIT_FACILITY_TYPES = [
  'HOME_CARE_OFFICE',
  'SERVICE_REGION',
  'HEAD_OFFICE',
  'DAY_CENTRE',
] as const;

export type SahAuditFacilityType = (typeof SAH_AUDIT_FACILITY_TYPES)[number];

/** Residential facility types in scope for the RAC audit program release. */
export const RAC_AUDIT_FACILITY_TYPES = [
  'RESIDENTIAL',
  'RESPITE_CENTRE',
  'RETIREMENT_VILLAGE',
] as const;

export type RacAuditFacilityType = (typeof RAC_AUDIT_FACILITY_TYPES)[number];

export const SAH_AUDIT_TYPES = [
  'SELF_ASSESSMENT',
  'INTERNAL_QUALITY',
  'EXTERNAL_ACCREDITATION',
] as const;

const AUDIT_TYPE_LABELS: Record<string, string> = {
  SELF_ASSESSMENT: 'Self-assessment',
  COMMISSION_SITE_AUDIT: 'Commission site audit',
  COMMISSION_REVIEW_AUDIT: 'Commission review audit',
  INTERNAL_QUALITY: 'Internal quality audit',
  EXTERNAL_ACCREDITATION: 'External accreditation',
};

/** Human label for an audit_schedules.audit_type; never returns the raw token. */
export function formatAuditTypeLabel(auditType: string): string {
  const known = AUDIT_TYPE_LABELS[auditType];
  if (known) return known;
  const words = auditType.toLowerCase().split('_').filter(Boolean).join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : auditType;
}

export const RAC_AUDIT_TYPES = [
  'SELF_ASSESSMENT',
  'INTERNAL_QUALITY',
  'COMMISSION_SITE_AUDIT',
  'COMMISSION_REVIEW_AUDIT',
  'EXTERNAL_ACCREDITATION',
] as const;

/**
 * @deprecated D1 fix (Wave 3 slice 3): this collapses a MIXED provider to a
 * single 'sah' phase, which silently dropped residential audit obligations
 * for mixed providers (docs/source-of-truth/success-criteria/OWNER-DECISIONS.md
 * D1). Scope-computing call sites (isAuditScheduleInProgramScope,
 * isAuditProgramPhaseActive, auditTypesForOrg, filterFacilitiesForAuditProgram)
 * now resolve against getAuditProgramPhases (plural) instead. This singular
 * function is kept, UNCHANGED, only for call sites that need one phase for
 * messaging/display and have not been migrated — it intentionally still
 * returns 'sah' for MIXED so those call sites keep today's exact wording.
 */
export function getAuditProgramPhase(
  orgType: AuditProgramScope | string | null | undefined,
): AuditProgramPhase {
  const normalised = String(orgType ?? '').toUpperCase();
  if (
    normalised === 'HOME_CARE' ||
    normalised === 'HOME_COMMUNITY' ||
    normalised === 'MIXED' ||
    normalised === 'MIXED_WORKSPACE'
  ) {
    return 'sah';
  }
  // Path-to-GO S10: residential tenants use the RAC audit program (no longer deferred).
  if (normalised === 'RESIDENTIAL') return 'rac';
  return 'unknown';
}

/**
 * All audit-program phases applicable to this org type. A MIXED provider runs
 * BOTH the residential (RAC) and Support at Home (SAH) audit regimes — a
 * mixed provider's residential homes are real registered facilities under
 * the Aged Care Act regardless of which programme workspace is selected, so
 * dropping the RAC phase for MIXED silently removed a legal obligation
 * surface. Scope-checking functions below (isAuditProgramPhaseActive,
 * auditTypesForOrg, filterFacilitiesForAuditProgram,
 * isAuditScheduleInProgramScope) resolve against this plural helper so a
 * MIXED tenant sees the union of both regimes everywhere audit scope is
 * computed from orgType.
 */
export function getAuditProgramPhases(
  orgType: AuditProgramScope | string | null | undefined,
): AuditProgramPhase[] {
  const normalised = String(orgType ?? '').toUpperCase();
  const phases: AuditProgramPhase[] = [];
  if (
    normalised === 'HOME_CARE' ||
    normalised === 'HOME_COMMUNITY' ||
    normalised === 'MIXED' ||
    normalised === 'MIXED_WORKSPACE'
  ) {
    phases.push('sah');
  }
  if (normalised === 'RESIDENTIAL' || normalised === 'MIXED' || normalised === 'MIXED_WORKSPACE') {
    phases.push('rac');
  }
  return phases;
}

/**
 * Resolve audit phases from the provider fact projection. This is deliberately
 * separate from the organisation-type helper: org type is a presentation
 * lens, while these scopes are authorised provider facts. Unknown facts stay
 * reviewable and never become an empty/no-obligation result.
 */
export function resolveAuditVisibility(
  context: AuditVisibilityContext | null | undefined,
): ResolvedAuditVisibility {
  const scopes = Array.from(
    new Set(
      (context?.auditScopes ?? context?.audit_scopes ?? []).filter(
        (scope): scope is AuditVisibilityScope =>
          scope === 'HOME_COMMUNITY' || scope === 'RESIDENTIAL',
      ),
    ),
  );
  const phases: AuditProgramPhase[] = [];
  if (scopes.includes('HOME_COMMUNITY')) phases.push('sah');
  if (scopes.includes('RESIDENTIAL')) phases.push('rac');
  const reviewState =
    context?.reviewState ??
    context?.review_state ??
    (scopes.length > 0 ? 'READY' : 'REVIEW_REQUIRED');
  return {
    scopes,
    phases,
    reviewState,
    showReview: reviewState !== 'READY' || scopes.length === 0,
  };
}

export function auditTypesForVisibility(
  context: AuditVisibilityContext | null | undefined,
): readonly string[] {
  const resolved = resolveAuditVisibility(context);
  const seen = new Set<string>();
  const types: string[] = [];
  for (const phase of resolved.phases) {
    for (const type of phase === 'sah' ? SAH_AUDIT_TYPES : RAC_AUDIT_TYPES) {
      if (!seen.has(type)) {
        seen.add(type);
        types.push(type);
      }
    }
  }
  return types;
}

export function isAuditScheduleInVisibilityScope(
  input: {
    auditType?: string | null;
    facilityType?: string | null;
    facilityId?: string | null;
  },
  context: AuditVisibilityContext | null | undefined,
): boolean {
  const resolved = resolveAuditVisibility(context);
  if (resolved.showReview && resolved.phases.length === 0) return false;
  return resolved.phases.some((phase) =>
    phase === 'sah' ? isAuditScheduleInSahScope(input) : isAuditScheduleInRacScope(input),
  );
}

export function filterFacilitiesForVisibility<T extends { facilityType?: string | null }>(
  facilities: T[],
  context: AuditVisibilityContext | null | undefined,
): T[] {
  const resolved = resolveAuditVisibility(context);
  return facilities.filter((facility) =>
    resolved.phases.some((phase) =>
      phase === 'sah'
        ? isSahAuditFacilityType(facility.facilityType ?? null)
        : isRacAuditFacilityType(facility.facilityType ?? null),
    ),
  );
}

export function isSahAuditFacilityType(facilityType: string | null | undefined): boolean {
  if (!facilityType) return false;
  return (SAH_AUDIT_FACILITY_TYPES as readonly string[]).includes(facilityType.toUpperCase());
}

export function isRacAuditFacilityType(facilityType: string | null | undefined): boolean {
  if (!facilityType) return false;
  return (RAC_AUDIT_FACILITY_TYPES as readonly string[]).includes(facilityType.toUpperCase());
}

export function isSahAuditType(auditType: string | null | undefined): boolean {
  if (!auditType) return false;
  return (SAH_AUDIT_TYPES as readonly string[]).includes(auditType.toUpperCase());
}

export function isRacAuditType(auditType: string | null | undefined): boolean {
  if (!auditType) return false;
  return (RAC_AUDIT_TYPES as readonly string[]).includes(auditType.toUpperCase());
}

export function isAuditScheduleInSahScope(input: {
  auditType?: string | null;
  facilityType?: string | null;
  facilityId?: string | null;
}): boolean {
  if (input.auditType != null && !isSahAuditType(input.auditType)) return false;
  if (!input.facilityId) return true;
  return isSahAuditFacilityType(input.facilityType);
}

export function isAuditScheduleInRacScope(input: {
  auditType?: string | null;
  facilityType?: string | null;
  facilityId?: string | null;
}): boolean {
  if (input.auditType != null && !isRacAuditType(input.auditType)) return false;
  if (!input.facilityId) return true;
  return isRacAuditFacilityType(input.facilityType);
}

/**
 * Phase-aware schedule scope (SAH and/or RAC release, per getAuditProgramPhases).
 * Requires resolved orgType. A MIXED tenant matches if EITHER release shape
 * accepts the row (D1 fix — previously collapsed to SAH only).
 */
export function isAuditScheduleInProgramScope(
  input: {
    auditType?: string | null;
    facilityType?: string | null;
    facilityId?: string | null;
  },
  orgType?: AuditProgramScope | string | null,
): boolean {
  const phases = getAuditProgramPhases(orgType);
  // Unknown / missing orgType: fail closed. Cron multi-tenant scans must use
  // isAuditScheduleInUnknownOrgUnionScope explicitly.
  if (phases.length === 0) return false;
  return phases.some((phase) =>
    phase === 'sah' ? isAuditScheduleInSahScope(input) : isAuditScheduleInRacScope(input),
  );
}

/**
 * Cron-only helper for platform-wide scans that lack a resolved per-tenant
 * orgType. Accepts either SAH or RAC release shapes (union). Do not use on
 * tenant-scoped reads — pass orgType to isAuditScheduleInProgramScope instead.
 */
export function isAuditScheduleInUnknownOrgUnionScope(input: {
  auditType?: string | null;
  facilityType?: string | null;
  facilityId?: string | null;
}): boolean {
  return isAuditScheduleInSahScope(input) || isAuditScheduleInRacScope(input);
}

export function isAuditProgramPhaseActive(
  orgType: AuditProgramScope | string | null | undefined,
): boolean {
  return getAuditProgramPhases(orgType).length > 0;
}

/** Union of audit types across every applicable phase (both, for MIXED). */
export function auditTypesForOrg(
  orgType: AuditProgramScope | string | null | undefined,
): readonly string[] {
  const phases = getAuditProgramPhases(orgType);
  const seen = new Set<string>();
  const types: string[] = [];
  for (const phase of phases) {
    for (const type of phase === 'sah' ? SAH_AUDIT_TYPES : RAC_AUDIT_TYPES) {
      if (!seen.has(type)) {
        seen.add(type);
        types.push(type);
      }
    }
  }
  return types;
}

/** Union of facility scope across every applicable phase (both, for MIXED). */
export function filterFacilitiesForAuditProgram<T extends { facilityType?: string | null }>(
  facilities: T[],
  orgType: AuditProgramScope | string | null | undefined,
): T[] {
  const phases = getAuditProgramPhases(orgType);
  return facilities.filter((facility) =>
    phases.some((phase) =>
      phase === 'sah'
        ? isSahAuditFacilityType(facility.facilityType ?? null)
        : isRacAuditFacilityType(facility.facilityType ?? null),
    ),
  );
}
